import { Col, type ColProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/fields/InputField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';

const defaultColSpan = { lg: 8, md: 12, xs: 24 };

export type HlfBankIntegrationProps = {
    version: 'v1' | 'v2';
    prefix?: string;
    colSpan?: ColProps;
    disabled?: boolean;
};

const fieldNames = [
    'allowedIps',
    'baseUrl',
    'submissionPartial',
    'editionPartial',
    'cancellationPartial',
    'reassignPartial',
    'publicCert',
    'privateKey',
    'dealerCode',
] as const;

const HlfBankIntegration = ({
    version,
    prefix,
    colSpan = defaultColSpan,
    disabled = false,
}: HlfBankIntegrationProps) => {
    const { t } = useTranslation(['bankDetails']);

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    return (
        <>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.allowedIps', { returnObjects: true })}
                    disabled={disabled}
                    name={names.allowedIps}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.hlfIntegration.baseUrl', { returnObjects: true })}
                    disabled={disabled}
                    name={names.baseUrl}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.hlfIntegration.submissionPartial', { returnObjects: true })}
                    disabled={disabled}
                    name={names.submissionPartial}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.hlfIntegration.editionPartial', { returnObjects: true })}
                    disabled={disabled}
                    name={names.editionPartial}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.hlfIntegration.cancellationPartial', { returnObjects: true })}
                    disabled={disabled}
                    name={names.cancellationPartial}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.hlfIntegration.reassignPartial', { returnObjects: true })}
                    disabled={disabled}
                    name={names.reassignPartial}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.dealerCodeForAudiAndSDM', { returnObjects: true })}
                    disabled={disabled}
                    name={names.dealerCode}
                    required={version === 'v2'}
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.hlfIntegration.privateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.privateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.hlfIntegration.publicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.publicCert}
                    required
                />
            </Col>
        </>
    );
};

export default HlfBankIntegration;
